import { useState, useEffect } from "react";
import {
  ButtonComponent,
  trans,
  HeaderComponent,
  RefreshButton,
} from "@comps/components";
import { makeRequest } from "@utils/helpers";
import { Commons, Const } from "../constants";
import { message } from 'antd';
import { FilterComponent, FilterButton, PaginationComponent } from "@comps/components";
import { downloadSalarySheetReq, downloadSalarySheetPdfReq, getAllDataReq, getFilterReq, downloadDepartmentSalarySheetReq } from "./requests";
import { AppstoreOutlined, FileExcelOutlined, FilePdfOutlined, FilterOutlined } from "@ant-design/icons";
import { handleDownloadFile } from "@utils/helpers";
import { Spin, Empty } from 'antd';

const langKey = Const["salarySheets"].lngKey;
const pageConfig = {
  headers: {
    title: trans(`${langKey}.moduleName`),
    breadcrumb: [
      {
        name: trans(`${langKey}.moduleName`),
        path: Const["salarySheets"].route
      }
    ]
  }
}
//global variables
var col_span_for_employee = 0;
var col_span_for_deduction = 0;
var col_span_for_earning = 0;
var col_span_for_benefit = 0;
var col_span_for_reimbursement = 0;
var filters = '';
const payitemTheme = {
  benefit: {
    sectionClass: "salary-sheet__group--benefit",
    headerClass: "salary-sheet__header--benefit",
  },
  earning: {
    sectionClass: "salary-sheet__group--earning",
    headerClass: "salary-sheet__header--earning",
  },
  deduction: {
    sectionClass: "salary-sheet__group--deduction",
    headerClass: "salary-sheet__header--deduction",
  },
  reimbursement: {
    sectionClass: "salary-sheet__group--reimbursement",
    headerClass: "salary-sheet__header--reimbursement",
  },
  totals: {
    sectionClass: "salary-sheet__group--totals",
    headerClass: "salary-sheet__header--totals",
  }
};

const formatAmount = (value) => {
  const numericValue = Number(value);
  if (Number.isNaN(numericValue)) {
    return value;
  }

  return numericValue.toFixed(2);
};

const getPreviewHeaderCards = (headerInfo = {}) => ([
  { label: 'Company Name', value: headerInfo.company_name || '-' },
  { label: 'Report Name', value: headerInfo.report_name || '-' },
  { label: 'Payroll Name', value: headerInfo.payroll_name || '-' },
  { label: 'Payroll Month', value: headerInfo.payroll_month || '-' },
  { label: 'Total Net Salaries', value: formatAmount(headerInfo.total_net_salaries || 0) },
]);

const getSectionTone = (group = '') => {
  const normalized = String(group).toLowerCase();
  if (normalized.includes('employee')) return 'employee';
  if (normalized.includes('earning')) return 'earning';
  if (normalized.includes('deduction')) return 'deduction';
  if (normalized.includes('reimbursement')) return 'reimbursement';
  if (normalized.includes('benefit')) return 'benefit';
  if (normalized.includes('attendance')) return 'attendance';
  if (normalized.includes('total')) return 'totals';
  return 'custom';
};

const normalizeColorValue = (color, fallback) => {
  if (typeof color !== 'string' || !color.trim()) return fallback;
  const value = color.trim();
  if (/^#?[0-9a-f]{6}$/i.test(value)) {
    return `#${value.replace('#', '').toUpperCase()}`;
  }
  return fallback;
};

const sectionFallbackStyle = (group = '') => {
  const tone = getSectionTone(group);
  const styles = {
    employee: { header_bg_color: '#1D4ED8', header_text_color: '#FFFFFF', cell_bg_color: '#EFF6FF', cell_text_color: '#1E3A8A', border_color: '#BFDBFE' },
    earning: { header_bg_color: '#16A34A', header_text_color: '#FFFFFF', cell_bg_color: '#F0FDF4', cell_text_color: '#064E3B', border_color: '#BBF7D0' },
    deduction: { header_bg_color: '#DC2626', header_text_color: '#FFFFFF', cell_bg_color: '#FEF2F2', cell_text_color: '#7F1D1D', border_color: '#FECACA' },
    reimbursement: { header_bg_color: '#0F766E', header_text_color: '#FFFFFF', cell_bg_color: '#F0FDFA', cell_text_color: '#134E4A', border_color: '#99F6E4' },
    benefit: { header_bg_color: '#0F9F6E', header_text_color: '#FFFFFF', cell_bg_color: '#ECFDF5', cell_text_color: '#065F46', border_color: '#A7F3D0' },
    attendance: { header_bg_color: '#7C3AED', header_text_color: '#FFFFFF', cell_bg_color: '#F5F3FF', cell_text_color: '#4C1D95', border_color: '#DDD6FE' },
    totals: { header_bg_color: '#111827', header_text_color: '#FFFFFF', cell_bg_color: '#F9FAFB', cell_text_color: '#111827', border_color: '#D1D5DB' },
    custom: { header_bg_color: '#475569', header_text_color: '#FFFFFF', cell_bg_color: '#F8FAFC', cell_text_color: '#334155', border_color: '#CBD5E1' },
  };
  return styles[tone] || styles.custom;
};

const getColumnSectionStyle = (column = {}) => {
  const fallback = sectionFallbackStyle(column.group);
  return {
    header_bg_color: normalizeColorValue(column.header_bg_color, fallback.header_bg_color),
    header_text_color: normalizeColorValue(column.header_text_color, fallback.header_text_color),
    cell_bg_color: normalizeColorValue(column.cell_bg_color, fallback.cell_bg_color),
    cell_text_color: normalizeColorValue(column.cell_text_color, fallback.cell_text_color),
    border_color: normalizeColorValue(column.border_color, fallback.border_color),
  };
};

const groupHeaderStyle = (group = {}) => ({
  '--salary-section-header-bg': normalizeColorValue(group.header_bg_color, sectionFallbackStyle(group.key).header_bg_color),
  '--salary-section-header-text': normalizeColorValue(group.header_text_color, '#FFFFFF'),
  '--salary-section-border': normalizeColorValue(group.border_color, sectionFallbackStyle(group.key).border_color),
});

const groupCellStyle = (columnOrGroup = {}) => {
  const style = getColumnSectionStyle(columnOrGroup);
  return {
    '--salary-section-cell-bg': style.cell_bg_color,
    '--salary-section-cell-text': style.cell_text_color,
    '--salary-section-border': style.border_color,
  };
};

const IndexSalarySheets = (props) => {
  const [dataSource, setDataSource] = useState([]);
  const [, setLoader] = useState(false);
  const [childComponent, setChildComponent] = useState(null);

  // const [filters, setFilters] = useState();
  const [loading, setLoading] = useState(false)
  const [payitems, setPayitems] = useState({})
  const [salarySheetColumns, setSalarySheetColumns] = useState([])
  const [headerInfo, setHeaderInfo] = useState({})
  const [selectedFilters, setSelectedFilters] = useState(props.initialFilters || '')
  const [currentPage, setCurrentPage] = useState(1)
  const [pagination, setPagination] = useState({
    "total": 0,
    "count": 0,
    "per_page": 0,
    "current_page": 0,
    "total_pages": 0,
    "links": {}
  });
  const hasSalaryData = dataSource.length > 0;
  const exportButtonsDisabled = !hasSalaryData || loading;

  // ==================================
  //   Use States End
  // ==================================

  // ==================================
  //   Table Data End
  // ==================================
  // ==================================
  //   Table Data End
  // ==================================

  useEffect(() => {
    if (props.embedded && props.initialFilters) {
      filters = props.initialFilters;
      setSelectedFilters(props.initialFilters);
      getAllData(props.initialFilters);
    }
    // eslint-disable-next-line
  }, [props.embedded, props.initialFilters]);

  useEffect(() => {
    getAllData(filters)
    // eslint-disable-next-line
  }, [currentPage]);

  const getAllData = (fil) => {

    if (!fil) {
      if (!props.embedded) {
        onOpenFilter()
      }
      return
    }
    setLoading(true)
    filters = fil
    setSelectedFilters(fil)
    setChildComponent(null)
    const payload = {
      page: currentPage,
      filters: filters
    }
    makeRequest(setLoader, getAllDataReq, payload, onSuccess, onGetAllDataError);
    onCloseFilter()
  };

  const onSuccess = (data, response) => {
    setPayitems(response.data.payitems)
    setSalarySheetColumns(response.data.salary_sheet_columns || [])
    setHeaderInfo(response.data.header_info || {})
    setDataSource(response.data.employments.data);
    setPagination(response.data.employments)
    if (response.data.employments.data.length < 1) {
      message.info('Salary Not Found with given filters');
      setLoading(false)
      onCloseFilter()
      return
    }
    if (response.message !== undefined) {

    }
    setLoading(false)
    onCloseFilter()
  };
  const onGetAllDataError = (err, res) => {
    setLoading(false)
    setHeaderInfo({})
    message.error(res.response.data.message);
    onCloseFilter()
  }

  const notifyEmptyExport = () => {
    message.info('Load salary data before exporting.');
  }
  const canExportSalarySheet = () => {
    if (hasSalaryData) {
      return true;
    }
    notifyEmptyExport();
    return false;
  }

  const onDownload = (type) => {
    if (!canExportSalarySheet()) {
      return;
    }
    setLoading(true)
    const payload = {
      filters: `${filters};download_type_|${type};`,
    }
    makeRequest(setLoader, downloadSalarySheetReq, payload, onDownloadSuccess, onDownloadSuccess);
    //makeRequestStateless(downloadSalarySheetReq, payload, onDownloadSuccess, onDownloadSuccess);
  }
  const onDownloadPdf = () => {
    if (!canExportSalarySheet()) {
      return;
    }
    setLoading(true)
    const payload = {
      filters: `${filters};`,
    }
    makeRequest(setLoader, downloadSalarySheetPdfReq, payload, onDownloadSuccess, onDownloadSuccess);
  }
  const onDownloadDepartmentWise = (type) => {
    if (!canExportSalarySheet()) {
      return;
    }
    setLoading(true)
    const payload = {
      filters: `${filters};download_type_|${type};`,
    }
    makeRequest(setLoader, downloadDepartmentSalarySheetReq, payload, onDownloadSuccess, onDownloadSuccess);
    //makeRequestStateless(downloadSalarySheetReq, payload, onDownloadSuccess, onDownloadSuccess);
  }

  const onDownloadSuccess = (err, res) => {
    setLoading(false)
    if (res.code === undefined) {
      const fileExtension = res.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ? 'xlsx' : 'pdf'
      const fileName = `SalarySheet.${fileExtension}`
      handleDownloadFile(fileName, res)
    }
    else {
      message.error(res.message)
    }
  }

  //---------------filters-----------------------
  const onOpenFilter = () => {
    setChildComponent(<FilterComponent filters={ filterFields } modkey={ props.modkey } selected={ selectedFilters } api={ getFilterReq } setCurrentPage={ setCurrentPage } onFilter={ onFilter } onCloseFilter={ onCloseFilter }
    />
    )
  }

  const onFilter = (filters) => {
    getAllData(filters)
  }
  const onCloseFilter = () => {
    setChildComponent(null);
  }
  const renderToolbarActions = (refreshFilters) => (
    <>
      <ButtonComponent
        text='PDF'
        icon={ <FilePdfOutlined /> }
        type='primary'
        onClick={ onDownloadPdf }
        disabled={ exportButtonsDisabled }
      />
      <ButtonComponent
        text='Excel'
        icon={ <FileExcelOutlined /> }
        type='primary'
        onClick={ () => onDownload('xlsx') }
        disabled={ exportButtonsDisabled }
      />
      <ButtonComponent
        text='Department Wise'
        icon={ <AppstoreOutlined /> }
        onClick={ () => onDownloadDepartmentWise('xlsx') }
        disabled={ exportButtonsDisabled }
      />
      <RefreshButton
        onClick={ () => getAllData(refreshFilters !== undefined ? refreshFilters : '') }
        disabled={ loading }
      />
      <FilterButton
        onClick={ onOpenFilter }
      />
    </>
  )
  //----end filter--------------------
  //get payitems
  const getPayitems = (each) => {
    col_span_for_benefit = payitems.benefit ? payitems.benefit.length : 0;
    col_span_for_earning = payitems.earning ? payitems.earning.length : 0;
    col_span_for_deduction = payitems.deduction ? payitems.deduction.length : 0;
    var html = employment(each);

    var benefits = 0;
    if (payitems.benefit) {
      col_span_for_benefit = payitems.benefit.length;
      payitems.benefit.forEach(function (row) {
        var payitem = each.computed[row.id];
        if (payitem) {
          benefits += payitem.amount;
          html += getTd(payitem.amount);
        } else {
          html += getTd("-");
        }
      });

      col_span_for_benefit++;
      if (each.cpl) {
        html += getTd(each.cpl);
      } else {
        html += getTd("-");
      }
    }

    var earnings = 0;
    if (payitems.earning) {
      col_span_for_earning = payitems.earning.length;
      payitems.earning.forEach(function (row) {
        var payitem = each.computed[row.id];
        if (payitem) {
          earnings += payitem.amount;
          html += getTd(payitem.amount);
        } else {
          html += getTd("-");
        }
      });

      col_span_for_earning++;
      if (each.cpl) {
        html += getTd(each.cpl);
      } else {
        html += getTd("-");
      }
    }


    var deductions = 0;
    if (payitems.deduction) {
      col_span_for_deduction = payitems.deduction.length;
      payitems.deduction.forEach(function (row) {
        var payitem = each.computed[row.id];
        if (payitem) {
          deductions += payitem.amount;
          if (payitem.details) {
            html += getTd(payitem.details);
          } else {
            html += getTd(payitem.amount);
          }
        } else {
          html += getTd("-");
        }
      });

      col_span_for_deduction++;
      if (each.quota) {
        html += getTd(each.quota);
      } else {
        html += getTd("-");
      }
    }
    var reimbursements = 0;
    if (payitems.reimbursement) {
      col_span_for_reimbursement = payitems.reimbursement.length;
      payitems.reimbursement.forEach(function (row) {
        var payitem = each.computed[row.id];
        if (payitem) {
          reimbursements += payitem.amount;
          html += getTd(payitem.details);
        } else {
          html += getTd("-");
        }
      });
    }
    html += total(each, earnings, deductions, reimbursements, benefits);
    return html;
  };
  const getDynamicValue = (row, key, index) => {
    if (key === 'sr_no') {
      return index + 1;
    }
    if (row[key] !== undefined) {
      return row[key];
    }
    if (row[String(key)] !== undefined) {
      return row[String(key)];
    }
    return '-';
  };
  const getDynamicGroups = () => {
    const groups = [];
    salarySheetColumns.forEach((column) => {
      const group = column.group || 'other';
      const label = column.group_label || group;
      const last = groups[groups.length - 1];
      if (last && last.key === group && last.label === label) {
        last.count += 1;
        return;
      }
      groups.push({
        key: group,
        label,
        count: 1,
        ...getColumnSectionStyle(column),
      });
    });
    return groups;
  };
  const getDynamicSectionOverview = () => getDynamicGroups().map((group) => ({
    ...group,
    tone: getSectionTone(group.key),
  }));
  const dynamicSectionClass = (group, prefix) => `${prefix} ${prefix}--${getSectionTone(group)}`;
  //get emplpoyee info
  function employment(each) {
    col_span_for_employee = 1;
    var html = '<td class="salary-sheet__employee-cell"><div class="salary-sheet__employee-identity">';
    if (each.profile_pic_url === "images/arnold-avatar.jpg") {
      each.profile_pic_url = '/images/arnold-avatar.jpg'
    }
    html += '<img width="40" height="40" class="salary-sheet__employee-avatar thumb img-circle rounded-circle" src="' + each.profile_pic_url + '" alt="' + each.full_name_code + '"/> ';
    html += '<div class="salary-sheet__employee-copy"><strong>' + each.full_name_code + '</strong>';
    html += '<span>' + each.designation_name + '</span></div>';
    html += "</div></td>";

    col_span_for_employee++;
    html += '<td class="salary-sheet__meta-cell">';
    html += each.branch_name;
    html += "</td>";

    col_span_for_employee++;
    html += '<td class="salary-sheet__meta-cell">';
    html += each.department_name;
    html += "</td>";

    col_span_for_employee++;
    html += '<td class="salary-sheet__meta-cell">';
    html += each.designation_name;
    html += "</td>";

    col_span_for_employee++;
    html += '<td class="salary-sheet__amount-cell salary-sheet__amount-cell--strong">';
    html += formatAmount(each.actual_salary);
    html += "</td>";

    // col_span_for_employee++;
    // html += "<td>";
    // html += each.no_of_days;
    // html += "</td>";

    // col_span_for_employee++;
    // html += "<td>";
    // html += each.worked_minutes + "minutes";
    // html += "</td>";
    col_span_for_employee++;
    html += '<td class="salary-sheet__status-cell"><span class="salary-sheet__status-badge label ' + each.payslip_class + '">';
    html += each.payslip_status;
    html += "</span></td>";

    return html;
  }

  // get td columns
  function getTd(amount) {

    if (Array.isArray(amount)) {
      var html = '<td class="salary-sheet__details-cell">';
      var total = 0;
      amount.forEach(function (row) {
        total += row.amount;
        html += '<div class="salary-sheet__detail-line"><span>' + row.name + '</span><strong>' + formatAmount(row.amount) + '</strong></div>';
      });
      html += '<div class="salary-sheet__detail-total"><span>Total</span><strong>' + formatAmount(total) + '</strong></div>';
      html += "</td>";
      if (total) {
        return html;
      }
      return '<td class="salary-sheet__amount-cell">0.00</td>';
    }
    if (amount === "-") {
      return '<td class="salary-sheet__empty-cell">' + amount + "</td>";
    }
    return '<td class="salary-sheet__amount-cell">' + formatAmount(amount) + "</td>";
  }

  //get total 
  function total(each, earnings, deductions, reimbursement, benefits) {
    var html = "";
    html += getTd(benefits);
    html += getTd(earnings);
    html += getTd(deductions);
    html += getTd(reimbursement);
    html += getTd(each.cash_salary);
    html += getTd(each.bank_salary);

    return html;
  }
  // payroll + month are required filters, so an empty sheet is almost always
  // "nothing selected yet" rather than a real no-results state
  const renderEmptyState = () => (
    <div
      style={ {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        minHeight: '320px',
        padding: '40px 20px',
      } }
    >
      <Empty
        image={ Empty.PRESENTED_IMAGE_SIMPLE }
        description={
          <span style={ { color: '#64748B' } }>
            <strong style={ { display: 'block', marginBottom: 6, color: '#0F172A', fontSize: 15 } }>
              { selectedFilters ? 'No salary sheet found' : 'No salary sheet to show yet' }
            </strong>
            { selectedFilters
              ? 'No salary data matches the selected filters. Change the payroll or month and try again.'
              : 'Apply filters to view the salary sheet. Payroll and Month are required.' }
          </span>
        }
      />
      <ButtonComponent
        text='Apply Filters'
        icon={ <FilterOutlined /> }
        type='primary'
        onClick={ onOpenFilter }
      />
    </div>
  )
  //
  return (
    <div className="salary-sheet-page">

      { childComponent }
      { props.embedded && (
        <div className='salary-sheet-toolbar salary-sheet-toolbar--embedded'>
          { renderToolbarActions(selectedFilters) }
        </div>
      ) }
      { !props.embedded && (
        <HeaderComponent headers={ pageConfig.headers } modkey={ props.modkey } className='salary-sheet-header'>
          { renderToolbarActions(filters) }
        </HeaderComponent>
      ) }
      <Spin spinning={ loading }>
        {
          selectedFilters && dataSource.length > 0 && salarySheetColumns.length > 0 &&
          <>
            <div className="salary-sheet__summary">
              { getPreviewHeaderCards(headerInfo).map((item) => (
                <div className="salary-sheet__summary-card" key={ item.label }>
                  <span className="salary-sheet__summary-label">{ item.label }</span>
                  <strong className="salary-sheet__summary-value">{ item.value }</strong>
                </div>
              )) }
            </div>
            <div className="salary-sheet__section-overview">
              { getDynamicSectionOverview().map((group) => (
                <div
                  className={ `salary-sheet__section-pill salary-sheet__section-pill--${group.tone} salary-sheet__section-pill--dynamic` }
                  style={ groupCellStyle(group) }
                  key={ `${group.key}-${group.label}` }
                >
                  <strong>{ group.label }</strong>
                  <span>{ group.count } Columns</span>
                </div>
              )) }
            </div>
            <div className="basetable-salarysheet-container salary-sheet__table-shell" style={ { marginTop: '20px' } }>
              <table className="table table-bordered table-responsive salary-sheet__table">
                <thead>
                  <tr className="group">
                    { getDynamicGroups().map((group) => (
                      <th
                        key={ `${group.key}-${group.label}` }
                        className={ `text-center ${dynamicSectionClass(group.key, 'salary-sheet__group')} salary-sheet__group--dynamic` }
                        style={ groupHeaderStyle(group) }
                        colSpan={ group.count }
                      >
                        <strong>{ group.label }</strong>
                        <small>{ group.count } columns</small>
                      </th>
                    )) }
                  </tr>
                  <tr>
                    { salarySheetColumns.map((column) => (
                      <th
                        key={ `${column.key}-${column.label}` }
                        className={ `${dynamicSectionClass(column.group, 'salary-sheet__header')} salary-sheet__header--dynamic` }
                        style={ { minWidth: '180px', ...groupCellStyle(column) } }
                      >
                        { column.label }
                      </th>
                    )) }
                  </tr>
                </thead>
                <tbody>
                  { dataSource.map((each, index) => (
                    <tr key={ each.id || each.employee_id || each.full_name_code }>
                      { salarySheetColumns.map((column) => (
                        <td
                          key={ `${each.id}-${column.key}` }
                          className={ `${Number.isFinite(Number(getDynamicValue(each, column.key, index))) ? 'salary-sheet__amount-cell' : 'salary-sheet__meta-cell'} salary-sheet__cell--${getSectionTone(column.group)} salary-sheet__cell--dynamic` }
                          style={ groupCellStyle(column) }
                        >
                          { getDynamicValue(each, column.key, index) }
                        </td>
                      )) }
                    </tr>
                  )) }
                </tbody>
              </table>
            </div>
          </>
        }
        {
          selectedFilters && dataSource.length > 0 && salarySheetColumns.length < 1 &&
          <>
            <div className="salary-sheet__summary">
              { getPreviewHeaderCards(headerInfo).map((item) => (
                <div className="salary-sheet__summary-card" key={ item.label }>
                  <span className="salary-sheet__summary-label">{ item.label }</span>
                  <strong className="salary-sheet__summary-value">{ item.value }</strong>
                </div>
              )) }
            </div>
            <div className="basetable-salarysheet-container salary-sheet__table-shell" style={ { marginTop: '20px' } }>
              <table className="table table-bordered table-responsive salary-sheet__table">
                <thead>
                  <tr className="group">
                    <th className="text-center salary-sheet__group salary-sheet__group--employee" colSpan={ col_span_for_employee }>{ trans(Commons.employee) }</th>

                    {
                      payitems.benefit &&
                      <th className={ `text-center salary-sheet__group ${payitemTheme.benefit.sectionClass}` } colSpan={ col_span_for_benefit }>
                        <strong>{ trans(`${langKey}.benefits`) }</strong>
                      </th>
                    }
                    {
                      payitems.earning &&
                      <th className={ `text-center salary-sheet__group ${payitemTheme.earning.sectionClass}` } colSpan={ col_span_for_earning }>
                        <strong>{ trans(`${langKey}.earnings`) }</strong>
                      </th>
                    }
                    {
                      payitems.deduction &&
                      <th className={ `text-center salary-sheet__group ${payitemTheme.deduction.sectionClass}` } colSpan={ col_span_for_deduction }>
                        <strong>{ trans(`${langKey}.deductions`) }</strong>
                      </th>
                    }
                    {
                      payitems.reimbursement &&
                      <th className={ `text-center salary-sheet__group ${payitemTheme.reimbursement.sectionClass}` } colSpan={ col_span_for_reimbursement }>
                        <strong>{ trans(`${langKey}.reimbursements`) }</strong>
                      </th>
                    }

                    <th className={ `text-center salary-sheet__group ${payitemTheme.totals.sectionClass}` } colSpan="6"><strong>{ trans(`${langKey}.totals`) }</strong></th>

                  </tr>
                  <tr>
                    <th className="w-100 salary-sheet__header salary-sheet__header--employee" style={ {
                      minWidth: '350px'
                    } } >{ trans(Commons.employee) }</th>
                    <th className="salary-sheet__header salary-sheet__header--employee">{ trans(Commons.branch) }</th>
                    <th className="salary-sheet__header salary-sheet__header--employee">{ trans(Commons.department) }</th>
                    <th className="salary-sheet__header salary-sheet__header--employee">{ trans(Commons.designation) }</th>
                    <th className="salary-sheet__header salary-sheet__header--employee" style={ {
                      minWidth: '150px'
                    } } >{ trans(`${langKey}.actual_salary`) }</th>
                    {/* <th>DAYS</th>
              <th>WORKING_HOURS</th> */}
                    <th className="salary-sheet__header salary-sheet__header--employee" style={ {
                      minWidth: '150px'
                    } }>{ trans(`${langKey}.status`) }</th>

                    { payitems.benefit !== undefined && payitems.benefit.map(payitem => (
                      <th key={ `benefit-${payitem.id}` } className={ `salary-sheet__header ${payitemTheme.benefit.headerClass}` } style={ {
                        minWidth: '200px'
                      } }>{ payitem.name }</th>
                    )) }

                    { payitems.benefit !== undefined &&
                      <th className={ `salary-sheet__header ${payitemTheme.benefit.headerClass}` }>CPL</th>
                    }
                    { payitems.earning !== undefined && payitems.earning.map(payitem => (
                      <th key={ `earning-${payitem.id}` } className={ `salary-sheet__header ${payitemTheme.earning.headerClass}` } style={ {
                        minWidth: '200px'
                      } }>{ payitem.name }</th>
                    )) }

                    { payitems.earning !== undefined &&

                      <th className={ `salary-sheet__header ${payitemTheme.earning.headerClass}` }>{ trans(`${langKey}.cpl`) }</th>
                    }
                    { payitems.deduction !== undefined && payitems.deduction.map(payitem => (
                      <th key={ `deduction-${payitem.id}` } className={ `salary-sheet__header ${payitemTheme.deduction.headerClass}` } style={ {
                        minWidth: '250px'
                      } }>{ payitem.name }</th>
                    )) }

                    {
                      payitems.deduction && <th className={ `salary-sheet__header ${payitemTheme.deduction.headerClass}` }>{ trans(`${langKey}.quota`) }</th>
                    }

                    { payitems.reimbursement !== undefined && payitems.reimbursement.map(payitem => (
                      <th key={ `reimbursement-${payitem.id}` } className={ `salary-sheet__header ${payitemTheme.reimbursement.headerClass}` }>{ payitem.name }</th>
                    )) }
                    <th className={ `salary-sheet__header ${payitemTheme.totals.headerClass}` } style={ {
                      minWidth: '150px'
                    } }>{ "Total Benefits" }</th>
                    <th className={ `salary-sheet__header ${payitemTheme.totals.headerClass}` } style={ {
                      minWidth: '150px'
                    } }>{ trans(`${langKey}.total_earnings`) }</th>
                    <th className={ `salary-sheet__header ${payitemTheme.totals.headerClass}` } style={ {
                      minWidth: '150px'
                    } }>{ trans(`${langKey}.total_deductions`) }</th>
                    <th className={ `salary-sheet__header ${payitemTheme.totals.headerClass}` }>{ trans(`${langKey}.reimbursements`) }</th>
                    {/* <th>{ trans(`${langKey}.net_pay`) }</th> */ }
                    <th className={ `salary-sheet__header ${payitemTheme.totals.headerClass}` }>{ "Net Cash" }</th>
                    <th className={ `salary-sheet__header ${payitemTheme.totals.headerClass}` }>{ "Net Bank" }</th>
                  </tr>
                </thead>
                <tbody>
                  { dataSource.length > 0 && dataSource.map(each => (
                    <tr key={ each.id || each.employee_id || each.full_name_code } dangerouslySetInnerHTML={ { __html: getPayitems(each) } } />
                  )) }

                </tbody>
              </table>

            </div>
          </>
        }
        { !loading && dataSource.length < 1 && renderEmptyState() }
      </Spin>
      { selectedFilters && dataSource.length > 0 && <PaginationComponent pagination={ pagination } modkey={ props.modkey } setCurrentPage={ setCurrentPage } currentPage={ currentPage } /> }
    </div>
  );
};

export default IndexSalarySheets;
const filterFields = [

  {
    type: "select",
    placeholder: 'Location',
    key: "location",
    data_key: "organization_structure_data.location",
  },
  {
    type: "select",
    placeholder: 'Branch',
    key: "branch",
    data_key: "organization_structure_data.branch",

  },
  {
    type: "select",
    placeholder: 'Departments',
    key: "department",
    data_key: "organization_structure_data.department",
    filterKey: "hierarchy_code"
  },
  {
    type: "select",
    placeholder: 'Employee',
    key: "employees",
    data_key: "supporting_data.employees",
    resource_type: 'employment'
    //is_resource: true
  },
  {
    type: "select",
    placeholder: 'Payroll',
    key: "payroll",
    data_key: "supporting_data.payroll",
    required: true
    //is_resource: true
  },
  {
    type: "date",
    placeholder: "Month",
    key: "month",
    required: true
  },

]
